import { useEffect, useState } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { getAuthHeaders } from "../utils/authUtils";
import { generatePageNumbers } from "../utils/pagination";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

export const Blogs = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 6,
    total: 0,
    total_pages: 1,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = "Our Blogs - Smart Scholars 360";
    AOS.init({ duration: 800, once: false });
    fetchBlogs();
  }, []);

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URI}/entities?category=blog&page=${page}&limit=${pagination.page_size}`,
        getAuthHeaders(),
      );
      if (res.data && res.data.data) {
        const transformedData = res.data.data.map((item) => ({
          id: item.id,
          img:
            item.data.images && item.data.images.length > 0
              ? item.data.images[0]
              : null,
          author: item.data.author,
          date: item.data.date,
          title: item.data.title,
          excerpt: item.data.excerpt,
        }));

        setBlogs(transformedData);
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      } else {
        setBlogs([]);
      }
      setError("");
    } catch (err) {
      setError("Failed to load blogs. Showing default content.");
      setBlogs([
        {
          id: 1,
          img: "https://www.dropbox.com/s/7d5qt5wb2xpqeww/city-street.jpg?raw=1",
          author: "By Jrom",
          date: "13 March 2025",
          title: "From the Other Side of the World",
          excerpt:
            "Lorem ipsum dolor sit, amet consectetur adipisicing elit. Mollitia odio dolorem amet, sunt magnam asperiores exercitationem consequuntur?",
        },
        {
          id: 2,
          img: "https://www.dropbox.com/s/7d5qt5wb2xpqeww/city-street.jpg?raw=1",
          author: "By Lina",
          date: "27 April 2025",
          title: "Lessons from the Sea Shore",
          excerpt:
            "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.",
        },
      ]);
      setPagination({ page: 1, page_size: 6, total: 2, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && !loading) {
      setExpandedIndex(null);
      fetchBlogs(newPage);
      document
        .querySelector(".Blog-body")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleReadMore = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const BlogSkeleton = () => (
    <div className="blog_card mb-3" style={{ opacity: 0.7 }}>
      <div className="img-container">
        <div
          style={{
            position: "relative",
            width: "300px",
            height: "200px",
            overflow: "hidden",
            borderRadius: "8px",
            background:
              "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
          }}
        />
      </div>
      <div className="blog_card_content">
        {[80, 60, 200, 100, 80, 90].map((w, i) => (
          <div
            key={i}
            style={{
              width: `${w}px`,
              height: i < 2 ? "16px" : i === 2 ? "24px" : "14px",
              borderRadius: "4px",
              marginBottom: "8px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
        ))}
      </div>
    </div>
  );

  if (loading && blogs.length === 0) {
    return (
      <>
        <div className="ss-inner-hero">
          <div className="container">
            <h1>Our Blogs</h1>
            <p>Insights, stories, and updates from Smart Scholars 360</p>
          </div>
        </div>
        <section className="Blog-body">
          <div className="container">
            <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
            {Array.from({ length: 6 }).map((_, i) => (
              <BlogSkeleton key={i} />
            ))}
          </div>
        </section>
      </>
    );
  }

  if (!loading && blogs.length === 0) {
    return (
      <>
        <div className="ss-inner-hero">
          <div className="container">
            <h1>Our Blogs</h1>
            <p>Insights, stories, and updates from Smart Scholars 360</p>
          </div>
        </div>
        <section className="Blog-body">
          <div className="container">
            <div style={{ textAlign: "center", padding: "50px" }}>
              <h3>No blogs available</h3>
              <p>Check back later for new content!</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <div className="ss-inner-hero">
        <div className="container">
          <h1>Our Blogs</h1>
          <p>Insights, stories, and updates from Smart Scholars 360</p>
        </div>
      </div>
      <section className="Blog-body">
        <div className="container">
          <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>

          {error && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}

          {loading && blogs.length > 0 && (
            <>
              {Array.from({ length: pagination.page_size }).map((_, i) => (
                <BlogSkeleton key={i} />
              ))}
            </>
          )}

          {!loading &&
            blogs.map((blog, index) => {
              const isExpanded = expandedIndex === index;
              const shortExcerpt =
                blog.excerpt && blog.excerpt.length > 100
                  ? blog.excerpt.slice(0, 100)
                  : blog.excerpt || "No excerpt available";

              return (
                <div className="blog_card mt-3" key={blog.id}>
                  <div
                    className="img-container"
                    style={{
                      width: "300px",
                      height: "200px",
                      overflow: "hidden",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ position: "relative" }}>
                      {blog.img ? (
                        <>
                          <img
                            src={blog.img}
                            alt={blog.title || `Blog ${index + 1}`}
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextElementSibling.style.display =
                                "flex";
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundColor: "#f8f9fa",
                              display: "none",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px dashed #dee2e6",
                              color: "#6c757d",
                              fontSize: "14px",
                            }}
                          >
                            📷 Image not available
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px dashed #dee2e6",
                            color: "#6c757d",
                            fontSize: "14px",
                          }}
                        >
                          📷 No image provided
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="blog_card_content">
                    <div className="blogC_top">
                      <p className="author">
                        {blog.author || "Unknown Author"}
                      </p>
                      <span className="blog-date">
                        {blog.date || "No Date"}
                      </span>
                    </div>
                    <h3 className="BlogC-H1">{blog.title || "Untitled"}</h3>
                    <p className="excerpt">
                      {isExpanded
                        ? blog.excerpt
                        : `${shortExcerpt}${
                            blog.excerpt && blog.excerpt.length > 100
                              ? "..."
                              : ""
                          }`}
                    </p>
                    {blog.excerpt && blog.excerpt.length > 100 && (
                      <button
                        className="BlogC_button btn btn-sm btn-outline-primary"
                        onClick={() => toggleReadMore(index)}
                      >
                        {isExpanded ? "Show less" : "Read more..."}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

          {pagination.total_pages > 1 && (
            <div style={{ textAlign: "center", marginTop: "30px" }}>
              <nav aria-label="Blog pagination">
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${
                      pagination.page === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || loading}
                    >
                      Previous
                    </button>
                  </li>
                  {generatePageNumbers(
                    pagination.page,
                    pagination.total_pages,
                  ).map((page, i) => (
                    <li
                      key={i}
                      className={`page-item ${
                        page === pagination.page ? "active" : ""
                      } ${page === "..." ? "disabled" : ""}`}
                    >
                      {page === "..." ? (
                        <span className="page-link">...</span>
                      ) : (
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </button>
                      )}
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      pagination.page === pagination.total_pages
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={
                        pagination.page === pagination.total_pages || loading
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
